import { useMemo } from 'react';
import { ObjectType } from 'api/pushes';
import {
  PhaseEntity,
  PhaseEntityType,
  converToEntityId,
  getId,
} from 'components/organisms/projects/project-plan/phase-entity';
import { FilterableField } from 'components/organisms/tasks/fields';
import { getFieldMapReducer } from 'routes/projects/list-v2/transformer';
import { floatUid } from 'utils/uid';
import {
  ProjectTemplateData,
  ProjectTemplateProjectPhase,
  ProjectTemplateSprint,
  ProjectTemplateTask,
} from '../types';
import { useGetAllProjectPhases } from './use-get-all-project-phases';
import { useGetAllProjectPhasesInTemplate } from './use-get-all-project-phases-in-template';
import { useGetAllSprints } from './use-get-all-sprints';
import { useGetAllTasks } from './use-get-all-tasks';

type FieldMapType = (
  hash: Record<string, unknown>,
  { fieldValue, fieldId }: { fieldId: number; fieldValue: unknown },
) => Record<string, unknown>;

const frameTaskObject = (task: ProjectTemplateTask, rootIndex: number) => ({
  key: `TASK-${task.taskId}`,
  id: task.taskId,
  title: task.taskName,
  startDay: task.startDay,
  duration: task.duration,
  assignee: task.assignee,
  private: task.private,
  data: task,
  rootIndex,
});

const frameSprintObject = (
  sprint: ProjectTemplateSprint,
  rootIndex: number,
) => ({
  key: `SPRINT-${sprint.sprintId}`,
  id: sprint.sprintId,
  title: sprint.sprintName,
  startDay: sprint.startDay,
  duration: sprint.duration,
  private: sprint.private,
  data: sprint,
  rootIndex,
});
const frameTaskObjectForBulkUpdate = (
  task: ProjectTemplateTask,
  rootIndex: number,
  toFieldMap: FieldMapType,
) => ({
  ...task,
  key: `TASK-${task.taskId}`,
  id: task.taskId,
  title: task.taskName,
  startDay: task.startDay,
  duration: task.duration,
  assignee: task.assignee,
  private: task.private,
  data: task,
  rootIndex,
  ...(task.fields ?? []).reduce(toFieldMap, {}),
});

const noop = () => ({});

const getEntityFromRef = <T extends PhaseEntity>(
  entity: T | number,
  lookup: { [key: string]: T },
) => {
  if (typeof entity === 'number') {
    return lookup[entity];
  }

  return entity;
};

const getEntityIdFromRef = <T extends PhaseEntity>(
  entity: T | number,
  entityType: PhaseEntityType,
) => {
  if (typeof entity === 'number') {
    return converToEntityId(entity, entityType);
  }

  return getId(entity, true);
};

type ConverterType =
  | typeof frameTaskObject
  | typeof frameTaskObjectForBulkUpdate;

const taskToChild = (
  taskRef: ProjectTemplateTask,
  {
    phaseIndex: index,
    allTasks,
    projectPhase,
  }: {
    phaseIndex: number;
    allTasks: {
      [key: string]: ProjectTemplateTask;
    };
    projectPhase: ProjectTemplateProjectPhase;
  },
) => {
  const task = getEntityFromRef(taskRef, allTasks);

  return {
    ...frameTaskObject(task, index),
    type: 'TASK',
    level: 1,
    parentTotal: projectPhase.tasks.length,
    children: task.subTasks?.map((subTaskRef) => {
      const subTask = getEntityFromRef(subTaskRef, allTasks);

      return {
        ...frameTaskObject(subTask, index),
        type: 'TASK',
        level: 2,
        parentTotal: task.subTasks.length,
        children: subTask.subTasks?.map((_subTaskRef) => {
          const _subTask = getEntityFromRef(_subTaskRef, allTasks);

          return {
            ...frameTaskObject(_subTask, index),
            parentTotal: subTask.subTasks.length,
            level: 3,
            type: 'TASK',
            children: [],
          };
        }),
      };
    }),
  };
};

const taskToRow = (
  taskRef: ProjectTemplateTask,
  {
    phaseIndex: index,
    allTasks,
    projectPhase,
    result,
    converter,
    toFieldMap,
  }: {
    phaseIndex: number;
    allTasks: {
      [key: string]: ProjectTemplateTask;
    };
    projectPhase: ProjectTemplateProjectPhase;
    result: any[];
    converter: ConverterType;
    toFieldMap: FieldMapType;
  },
) => {
  const task = getEntityFromRef(taskRef, allTasks);

  const phaseTask = {
    ...converter(task, index, toFieldMap),
    type: 'TASK',
    level: 1,
    parentTotal: projectPhase.tasks.length,
    hierarchy: [projectPhase.projectPhaseId, task.taskId].map(String),
  };

  result.push(phaseTask);

  task.subTasks?.forEach((subTaskRef) => {
    const subTask = getEntityFromRef(subTaskRef, allTasks);

    const taskSubTask = {
      ...converter(subTask, index, toFieldMap),
      type: 'TASK',
      level: 2,
      parentTotal: task.subTasks.length,
      hierarchy: [projectPhase.projectPhaseId, task.taskId, subTask.taskId].map(
        String,
      ),
    };

    result.push(taskSubTask);

    subTask.subTasks?.forEach((_subTaskRef) => {
      const _subTask = getEntityFromRef(_subTaskRef, allTasks);

      const secondLevelSubTask = {
        ...converter(_subTask, index, toFieldMap),
        parentTotal: subTask.subTasks.length,
        level: 3,
        type: 'TASK',
        hierarchy: [
          projectPhase.projectPhaseId,
          task.taskId,
          subTask.taskId,
          _subTask.taskId,
        ].map(String),
      };

      result.push(secondLevelSubTask);
    });
  });
};

const getPhaseSprint = ({
  sprint,
  projectPhase,
  index,
}: {
  sprint: ProjectTemplateSprint;
  projectPhase: ProjectTemplateProjectPhase;
  index: number;
}): ProjectTemplateData => ({
  ...frameSprintObject(sprint ?? {}, index),
  type: ObjectType.Sprint,
  level: 1,
  parentTotal: projectPhase.sprints?.length,
  hierarchy: [projectPhase.projectPhaseId, sprint.sprintId].map(String),
});

const addSprintEntity = ({
  projectPhase,
  allSprints,
  index,
  entitiesMap,
  isAgGrid,
}: {
  projectPhase: ProjectTemplateProjectPhase;
  allSprints: { [key: string]: ProjectTemplateSprint };
  entitiesMap: Record<string, ProjectTemplateData | ProjectTemplateData[]>;
  index: number;
  isAgGrid?: boolean;
}) => {
  projectPhase.sprints?.forEach((sprintRef) => {
    const key = getEntityIdFromRef(sprintRef, PhaseEntityType.Sprint);

    const sprint = getEntityFromRef(sprintRef, allSprints);

    const phaseSprint = getPhaseSprint({
      sprint: sprint ?? {},
      projectPhase,
      index,
    });

    entitiesMap[key] = isAgGrid ? [phaseSprint] : phaseSprint;
  });
};

export const useTransformTabularData = (
  phases?: ProjectTemplateProjectPhase[] | null,
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();
  const allSprints = useGetAllSprints();

  return useMemo(() => {
    const _phases =
      phases !== undefined && phases !== null
        ? phases || []
        : (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    return _phases.map((projectPhase, index) => {
      const entitiesMap = {};
      const order = projectPhase.entityOrder ?? [];

      projectPhase.tasks.forEach((taskRef) => {
        const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);

        if (!key) {
          return;
        }

        entitiesMap[key] = taskToChild(taskRef, {
          phaseIndex: index,
          allTasks,
          projectPhase,
        });
      });

      addSprintEntity({
        projectPhase,
        allSprints,
        entitiesMap,
        index,
      });

      const entities = order.map((id) => entitiesMap[id]).filter(Boolean);

      return {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        children: entities,
      };
    });
  }, [projectPhaseIds, projectPhases, phases, allTasks, allSprints]);
};

export const useTransformTabularDataForAgGrid = (
  phases?: ProjectTemplateProjectPhase[],
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();
  const allSprints = useGetAllSprints();

  return useMemo(() => {
    const _phases =
      phases !== undefined && phases !== null
        ? phases || []
        : (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    const result: any[] = [];

    _phases.forEach((projectPhase, index) => {
      const phase = {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        hierarchy: [projectPhase.projectPhaseId].map(String),
      };

      result.push(phase);
      const entitiesMap = {};
      const order = projectPhase.entityOrder ?? [];

      projectPhase.tasks.forEach((taskRef) => {
        const taskEntity: any[] = [];
        const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);

        if (!key) {
          return;
        }

        taskToRow(taskRef, {
          phaseIndex: index,
          allTasks,
          projectPhase,
          result: taskEntity,
          converter: frameTaskObject,
          toFieldMap: noop,
        });

        entitiesMap[key] = taskEntity;
      });

      addSprintEntity({
        projectPhase,
        allSprints,
        entitiesMap,
        index,
        isAgGrid: true,
      });

      const entities = order.map((id) => entitiesMap[id]).filter(Boolean);

      result.push(...entities.flat());
    });

    const newPhaseId = floatUid();
    const hasNewPhaseRow = _phases?.[_phases?.length - 1]?.isNew;

    if (hasNewPhaseRow) {
      return result;
    }
    result.push({
      key: `PROJECT_PHASE-${newPhaseId}`,
      id: newPhaseId,
      type: 'PROJECT_PHASE',
      level: 0,
      data: {
        isNew: true,
        entityOrder: [],
      },
      rootIndex: _phases.length,
      hierarchy: [`${newPhaseId}`],
      insertAt: null,
    });

    return result;
  }, [projectPhaseIds, projectPhases, allTasks, phases, allSprints]);
};

export const useTransformTabularDataForBulkTaskGrid = (
  lookup: Record<number, FilterableField>,
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();
  const allSprints = useGetAllSprints();
  const toFieldMap = getFieldMapReducer(lookup);

  return useMemo(() => {
    const _phases = (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    const result: any[] = [];

    _phases.forEach((projectPhase, index) => {
      const phase = {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        hierarchy: [projectPhase.projectPhaseId].map(String),
      };

      result.push(phase);

      const entitiesMap = {};
      const order = projectPhase.entityOrder ?? [];

      projectPhase.tasks.forEach((taskRef) => {
        const taskEntity: any[] = [];
        const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);

        if (!key) {
          return;
        }

        taskToRow(taskRef, {
          phaseIndex: index,
          allTasks,
          projectPhase,
          result: taskEntity,
          converter: frameTaskObjectForBulkUpdate,
          toFieldMap,
        });

        entitiesMap[key] = taskEntity;
      });

      addSprintEntity({
        projectPhase,
        allSprints,
        entitiesMap,
        index,
        isAgGrid: true,
      });

      const entities = order.map((id) => entitiesMap[id]).filter(Boolean);

      result.push(...entities.flat());
    });

    const hasNewPhaseRow = _phases?.[_phases?.length - 1]?.isNew;

    if (hasNewPhaseRow) {
      return result;
    }
    const newPhaseId = floatUid();

    result.push({
      key: `PROJECT_PHASE-${newPhaseId}`,
      id: newPhaseId,
      type: 'PROJECT_PHASE',
      level: 0,
      data: {
        isNew: true,
        entityOrder: [],
      },
      rootIndex: _phases.length,
      hierarchy: [`${newPhaseId}`],
      insertAt: null,
    });

    return result;
  }, [projectPhaseIds, projectPhases, allTasks, toFieldMap, allSprints]);
};
